/** Tolerância de comparação. Abaixo de um sample a 48 kHz (≈2.1e-5 s). */
export const EPSILON = 1e-6;
export const FPS_30 = { num: 30, den: 1 };
export const FPS_25 = { num: 25, den: 1 };
export const FPS_24 = { num: 24, den: 1 };
export const FPS_2997 = { num: 30000, den: 1001 };
export const FPS_5994 = { num: 60000, den: 1001 };
export const FPS_60 = { num: 60, den: 1 };
export function fpsToNumber(fps) {
    return fps.num / fps.den;
}
/** Duração de um frame, em segundos. */
export function frameDuration(fps) {
    return fps.den / fps.num;
}
export function secondsToFrames(seconds, fps) {
    return Math.round((seconds * fps.num) / fps.den);
}
export function framesToSeconds(frames, fps) {
    return (frames * fps.den) / fps.num;
}
/**
 * Trava um tempo na grade de frames. Toda mutação da timeline passa por aqui — é o que
 * torna a paridade preview↔render estrutural em vez de acidental.
 */
export function quantize(seconds, fps) {
    if (!Number.isFinite(seconds))
        return 0;
    return framesToSeconds(secondsToFrames(seconds, fps), fps);
}
/** Quantização de áudio: precisa ao sample, não ao frame. */
export function quantizeAudio(seconds, sampleRate) {
    if (!Number.isFinite(seconds))
        return 0;
    return Math.round(seconds * sampleRate) / sampleRate;
}
/** Arredonda para cima até o próximo limite de frame. */
export function quantizeCeil(seconds, fps) {
    return framesToSeconds(Math.ceil((seconds * fps.num) / fps.den - EPSILON), fps);
}
/** Arredonda para baixo até o limite de frame anterior. */
export function quantizeFloor(seconds, fps) {
    return framesToSeconds(Math.floor((seconds * fps.num) / fps.den + EPSILON), fps);
}
export function eq(a, b, eps = EPSILON) {
    return Math.abs(a - b) < eps;
}
export function lt(a, b, eps = EPSILON) {
    return a < b - eps;
}
export function lte(a, b, eps = EPSILON) {
    return a < b + eps;
}
export function gt(a, b, eps = EPSILON) {
    return a > b + eps;
}
export function gte(a, b, eps = EPSILON) {
    return a > b - eps;
}
export function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}
/** Intervalos [start,end) se sobrepõem com área positiva? */
export function rangesOverlap(aStart, aEnd, bStart, bEnd) {
    return lt(aStart, bEnd) && lt(bStart, aEnd);
}
export function rangeContains(start, end, time) {
    return gte(time, start) && lt(time, end);
}
/** Interseção de dois intervalos, ou null se não houver área positiva. */
export function intersectRanges(aStart, aEnd, bStart, bEnd) {
    const start = Math.max(aStart, bStart);
    const end = Math.min(aEnd, bEnd);
    return lt(start, end) ? { start, end } : null;
}
/** Formata como HH:MM:SS:FF (timecode de edição). */
export function formatTimecode(seconds, fps) {
    const totalFrames = secondsToFrames(Math.max(0, seconds), fps);
    const fpsInt = Math.round(fpsToNumber(fps));
    const frames = totalFrames % fpsInt;
    const totalSeconds = Math.floor(totalFrames / fpsInt);
    const s = totalSeconds % 60;
    const m = Math.floor(totalSeconds / 60) % 60;
    const h = Math.floor(totalSeconds / 3600);
    const p = (n) => String(n).padStart(2, '0');
    return `${p(h)}:${p(m)}:${p(s)}:${p(frames)}`;
}
/** Formata como MM:SS.mmm (leitura humana, painéis de transcrição). */
export function formatTime(seconds, showMs = true) {
    const safe = Math.max(0, seconds);
    const ms = Math.floor((safe % 1) * 1000);
    const total = Math.floor(safe);
    const s = total % 60;
    const m = Math.floor(total / 60) % 60;
    const h = Math.floor(total / 3600);
    const p = (n) => String(n).padStart(2, '0');
    const base = h > 0 ? `${h}:${p(m)}:${p(s)}` : `${p(m)}:${p(s)}`;
    return showMs ? `${base}.${String(ms).padStart(3, '0')}` : base;
}
/** Aceita "12.42", "1:02", "00:01:02:15" e devolve segundos. */
export function parseTimecode(input, fps) {
    const trimmed = input.trim();
    if (/^\d+(\.\d+)?$/.test(trimmed))
        return parseFloat(trimmed);
    const parts = trimmed.split(':');
    if (parts.length === 2) {
        const [m, s] = parts;
        const mm = Number(m);
        const ss = Number(s);
        if (!Number.isFinite(mm) || !Number.isFinite(ss))
            return null;
        return mm * 60 + ss;
    }
    if (parts.length === 3) {
        const [h, m, s] = parts;
        const nums = [h, m, s].map(Number);
        if (nums.some((n) => !Number.isFinite(n)))
            return null;
        return nums[0] * 3600 + nums[1] * 60 + nums[2];
    }
    if (parts.length === 4) {
        const nums = parts.map(Number);
        if (nums.some((n) => !Number.isFinite(n)))
            return null;
        return (nums[0] * 3600 + nums[1] * 60 + nums[2] + framesToSeconds(nums[3], fps));
    }
    return null;
}
/** Une intervalos sobrepostos ou separados por menos de `gap`. */
export function mergeRanges(ranges, gap = 0) {
    if (ranges.length === 0)
        return [];
    const sorted = [...ranges].sort((a, b) => a.start - b.start);
    const out = [];
    let current = { ...sorted[0] };
    for (let i = 1; i < sorted.length; i++) {
        const next = sorted[i];
        if (lte(next.start, current.end + gap)) {
            current.end = Math.max(current.end, next.end);
        }
        else {
            out.push(current);
            current = { ...next };
        }
    }
    out.push(current);
    return out;
}
/** Complemento de `ranges` dentro de [start,end) — o que sobra depois de remover. */
export function invertRanges(ranges, start, end) {
    const merged = mergeRanges(ranges.filter((r) => rangesOverlap(r.start, r.end, start, end)));
    const out = [];
    let cursor = start;
    for (const r of merged) {
        const s = Math.max(r.start, start);
        if (gt(s, cursor))
            out.push({ start: cursor, end: s });
        cursor = Math.max(cursor, Math.min(r.end, end));
    }
    if (lt(cursor, end))
        out.push({ start: cursor, end });
    return out;
}
//# sourceMappingURL=time.js.map